<template>
  <div class="auth-wrapper auth-v2 register-page container">
    <b-row class="auth-inner m-0">
      <b-link class="brand-logo" />

      <b-col
        lg="7"
        class="d-none d-lg-flex align-items-center p-4 p-xl-5 register-left"
      >
        <div class="register-left-art">
          <b-img
            fluid
            :src="imgUrl"
            alt="Register Illustration"
            class="register-illustration"
          />
        </div>
      </b-col>

      <b-col
        lg="5"
        class="d-flex align-items-center auth-bg px-2 py-2 py-lg-4"
      >
        <b-col
          sm="11"
          md="9"
          lg="12"
          class="mx-auto register-form-wrap"
        >
          <div class="text-center mb-2">
            <b-img
              width="120"
              fluid
              src="/logo.png"
              alt="My PODA"
            />
          </div>

          <b-card-title
            class="register-title mb-25"
            title-tag="h2"
          >
            Welcome to My PODA 👋
          </b-card-title>
          <b-card-text class="register-subtitle mb-2">
            Create a new account
          </b-card-text>

          <validation-observer
            ref="registerForm"
            #default="{ invalid }"
          >
            <b-form
              class="auth-register-form register-form mt-1"
              @submit.prevent="register"
            >
              <b-alert
                v-if="error"
                variant="danger"
                show
                class="mb-1"
              >
                {{ error }}
              </b-alert>

              <b-form-group
                label="Company Name"
                label-for="register-company-name"
                class="mb-50"
              >
                <b-form-input
                  id="register-company-name"
                  v-model="companyName"
                  placeholder="Your company name"
                />
              </b-form-group>

              <b-form-group
                label="Contact Person Name"
                label-for="register-contact-person"
                class="mb-50"
              >
                <b-form-input
                  id="register-contact-person"
                  v-model="contactPersonName"
                  placeholder="Your company name"
                />
              </b-form-group>

              <b-form-group
                label="Address"
                label-for="register-address"
                class="mb-50"
              >
                <b-form-input
                  id="register-address"
                  v-model="address"
                  placeholder="Your company name"
                />
              </b-form-group>

              <b-row class="compact-row">
                <b-col cols="6">
                  <b-form-group
                    label="Zip Code"
                    label-for="register-zip-code"
                    class="mb-50"
                  >
                    <b-form-input
                      id="register-zip-code"
                      v-model="pinCode"
                      placeholder="123456"
                    />
                  </b-form-group>
                </b-col>
                <b-col cols="6">
                  <b-form-group
                    label="Country"
                    label-for="register-country"
                    class="mb-50"
                  >
                    <b-form-select
                      id="register-country"
                      v-model="country"
                      :options="countryOptions"
                    />
                  </b-form-group>
                </b-col>
              </b-row>

              <b-form-group
                label="State of Incorporation"
                label-for="register-state"
                class="mb-50"
              >
                <b-form-input
                  id="register-state"
                  v-model="stateOfIncorporation"
                  placeholder="e.g. Delaware"
                />
              </b-form-group>

              <b-row class="compact-row align-items-end">
                <b-col cols="8">
                  <b-form-group
                    label="Email"
                    label-for="register-email"
                    class="mb-50"
                  >
                    <validation-provider
                      #default="{ errors }"
                      name="Email"
                      vid="email"
                      rules="required|email"
                    >
                      <b-form-input
                        id="register-email"
                        v-model="userEmail"
                        :state="errors.length > 0 ? false : null"
                        placeholder="Email"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>
                <b-col cols="4">
                  <b-button
                    type="button"
                    class="verify-btn"
                    block
                    :disabled="otpSending || isEmailVerifiedNow"
                    @click.stop.prevent="verifyEmail"
                  >
                    <b-spinner v-if="otpSending && otpChannel === 'email'" small class="mr-50" />
                    {{ isEmailVerifiedNow ? 'Verified' : 'Verify Email' }}
                  </b-button>
                </b-col>
              </b-row>

              <b-row class="compact-row">
                <b-col cols="6">
                  <b-form-group
                    label="Create Password"
                    label-for="register-password"
                    class="mb-1"
                  >
                    <validation-provider
                      #default="{ errors }"
                      name="Password"
                      vid="password"
                      rules="required"
                    >
                      <b-input-group class="input-group-merge">
                        <b-form-input
                          id="register-password"
                          v-model="password"
                          :state="errors.length > 0 ? false : null"
                          placeholder="saleb@1234"
                          :type="passwordFieldType"
                        />
                        <b-input-group-append is-text>
                          <feather-icon
                            class="cursor-pointer"
                            :icon="passwordToggleIcon"
                            @click="togglePasswordVisibility"
                          />
                        </b-input-group-append>
                      </b-input-group>
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>
                <b-col cols="6">
                  <b-form-group
                    label="Confirm Password"
                    label-for="register-confirm-password"
                    class="mb-1"
                  >
                    <b-input-group class="input-group-merge">
                      <b-form-input
                        id="register-confirm-password"
                        v-model="confirmPassword"
                        placeholder="Password"
                        :type="confirmPasswordFieldType"
                      />
                      <b-input-group-append is-text>
                        <feather-icon
                          class="cursor-pointer"
                          :icon="confirmPasswordToggleIcon"
                          @click="toggleConfirmPasswordVisibility"
                        />
                      </b-input-group-append>
                    </b-input-group>
                  </b-form-group>
                </b-col>
              </b-row>

              <b-button
                block
                type="submit"
                :disabled="invalid || isRegistering"
                class="register-submit-btn mt-75"
              >
                <b-spinner v-if="isRegistering" small class="mr-50" />
                Register
              </b-button>
            </b-form>
          </validation-observer>

          <p class="text-center auth-footer-text register-login-text">
            Already company registered?
            <router-link
              :to="{ name: 'auth-login' }"
              class="auth-login-link-btn"
            >
              Log in
            </router-link>
          </p>
        </b-col>
      </b-col>
    </b-row>

    <vendor-outcome-modal
      :visible.sync="showRegisteredModal"
      variant="company-registered"
      @primary="onRegisteredPrimary"
    />

    <b-modal
      v-model="otpModalOpen"
      centered
      no-close-on-backdrop
      modal-class="register-otp-modal"
      title="Enter verification code"
      @hidden="resetOtpModal"
    >
      <p class="mb-1">
        {{ otpModalHint }}
      </p>
      <b-form-input
        v-model="otpCode"
        maxlength="6"
        inputmode="numeric"
        placeholder="6-digit code"
        autocomplete="one-time-code"
        @keyup.enter="submitOtpVerification"
      />
      <b-alert
        v-if="otpError"
        variant="danger"
        show
        class="mt-1 mb-0"
      >
        {{ otpError }}
      </b-alert>
      <p class="mt-1 mb-0 text-center">
        <span v-if="otpResendSeconds > 0" class="text-muted small">
          Resend code in {{ otpResendSeconds }}s
        </span>
        <b-button
          v-else
          variant="link"
          class="p-0"
          :disabled="otpSending"
          @click="resendOtpCode"
        >
          Resend code
        </b-button>
      </p>
      <template #modal-footer>
        <b-button variant="outline-secondary" :disabled="otpVerifying" @click="otpModalOpen = false">
          Cancel
        </b-button>
        <b-button variant="primary" :disabled="otpVerifying || otpCode.trim().length < 6" @click="submitOtpVerification">
          <b-spinner v-if="otpVerifying" small class="mr-50" />
          Verify
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
/* eslint-disable global-require */
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import {
  BRow,
  BCol,
  BLink,
  BButton,
  BForm,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BInputGroup,
  BInputGroupAppend,
  BImg,
  BCardTitle,
  BCardText,
  BAlert,
  BModal,
  BSpinner,
} from 'bootstrap-vue'
import { required, email } from '@validations'
import store from '@/store/index'
import axios from '@/component/axios'
import jwtDefaultConfig from '@core/auth/jwt/jwtDefaultConfig'
import VENDOR_COUNTRY_OPTIONS from '@/constants/countries'
import { notifyError, notifyInfo, notifySuccess } from '@/utils/uiActions'
import VendorOutcomeModal from '@/components/vendor/VendorOutcomeModal.vue'

export default {
  components: {
    BRow,
    BImg,
    BCol,
    BLink,
    BButton,
    BForm,
    BAlert,
    BCardText,
    BCardTitle,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BInputGroup,
    BInputGroupAppend,
    ValidationProvider,
    ValidationObserver,
    BModal,
    BSpinner,
    VendorOutcomeModal,
  },
  data() {
    return {
      showRegisteredModal: false,
      companyName: '',
      contactPersonName: '',
      address: '',
      pinCode: '',
      country: 'India',
      stateOfIncorporation: '',
      userEmail: '',
      password: '',
      confirmPassword: '',
      passwordFieldType: 'password',
      confirmPasswordFieldType: 'password',
      sideImg: require('@/assets/images/pages/register-v2.svg'),
      error: '',
      required,
      email,
      emailVerified: false,
      verifiedEmailKey: '',
      otpModalOpen: false,
      otpChannel: 'email',
      otpCode: '',
      otpModalHint: '',
      otpError: '',
      otpVerifying: false,
      otpSending: false,
      otpResendSeconds: 0,
      otpResendTimerId: null,
      isRegistering: false,
    }
  },
  beforeDestroy() {
    this.clearOtpResendTimer()
  },
  computed: {
    imgUrl() {
      if (store.state.appConfig.layout.skin === 'dark') {
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.sideImg = require('@/assets/images/pages/register-v2-dark.svg')
        return this.sideImg
      }
      return this.sideImg
    },
    passwordToggleIcon() {
      return this.passwordFieldType === 'password' ? 'EyeIcon' : 'EyeOffIcon'
    },
    confirmPasswordToggleIcon() {
      return this.confirmPasswordFieldType === 'password' ? 'EyeIcon' : 'EyeOffIcon'
    },
    normalizedEmail() {
      return String(this.userEmail || '').trim().toLowerCase()
    },
    isEmailVerifiedNow() {
      return this.emailVerified && this.verifiedEmailKey === this.normalizedEmail
    },
    countryOptions() {
      return VENDOR_COUNTRY_OPTIONS
    },
  },
  methods: {
    togglePasswordVisibility() {
      this.passwordFieldType = this.passwordFieldType === 'password' ? 'text' : 'password'
    },
    toggleConfirmPasswordVisibility() {
      this.confirmPasswordFieldType = this.confirmPasswordFieldType === 'password' ? 'text' : 'password'
    },
    isValidEmail(value) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim())
    },
    markEmailVerified(email) {
      this.emailVerified = true
      this.verifiedEmailKey = email
      this.otpModalOpen = false
      notifySuccess(this, 'Email verified. You can continue registration.')
    },
    async verifyEmail() {
      this.error = ''
      this.otpError = ''
      const email = String(this.userEmail || '').trim().toLowerCase()
      if (!this.isValidEmail(email)) {
        const message = 'Enter a valid email address before verifying.'
        this.error = message
        notifyError(this, message)
        return
      }
      if (this.emailVerified && this.verifiedEmailKey === email) {
        notifyInfo(this, 'This email is already verified.')
        return
      }
      this.otpChannel = 'email'
      this.otpSending = true
      try {
        const res = await axios.post(jwtDefaultConfig.sendEmailOtpEndpoint, { email })
        if (res.data?.success) {
          this.otpCode = ''
          this.otpError = ''
          this.otpModalHint = `Enter the 6-digit code sent to ${email}.`
          this.otpModalOpen = true
          this.startOtpResendTimer()
          notifyInfo(this, res.data?.message || 'Verification code sent to your email.')
        } else {
          const message = res.data?.message || 'Unable to send email verification code.'
          this.error = message
          notifyError(this, message)
        }
      } catch (e) {
        const message = e.response?.data?.message || 'Unable to send email verification code.'
        this.error = message
        notifyError(this, message)
      } finally {
        this.otpSending = false
      }
    },
    resetOtpModal() {
      this.otpCode = ''
      this.otpError = ''
      this.otpVerifying = false
      this.clearOtpResendTimer()
    },
    startOtpResendTimer() {
      this.clearOtpResendTimer()
      this.otpResendSeconds = 60
      this.otpResendTimerId = window.setInterval(() => {
        if (this.otpResendSeconds <= 1) {
          this.otpResendSeconds = 0
          this.clearOtpResendTimer()
          return
        }
        this.otpResendSeconds -= 1
      }, 1000)
    },
    clearOtpResendTimer() {
      if (this.otpResendTimerId) {
        window.clearInterval(this.otpResendTimerId)
        this.otpResendTimerId = null
      }
    },
    async resendOtpCode() {
      if (this.otpResendSeconds > 0 || this.otpSending) return
      await this.verifyEmail()
    },
    async submitOtpVerification() {
      const otp = String(this.otpCode || '').trim()
      if (otp.length < 6) {
        this.otpError = 'Enter the full 6-digit verification code.'
        return
      }
      this.otpVerifying = true
      this.otpError = ''
      this.error = ''
      try {
        const email = String(this.userEmail || '').trim().toLowerCase()
        const res = await axios.post(jwtDefaultConfig.verifyEmailOtpEndpoint, { email, otp })
        if (res.data?.success) {
          this.markEmailVerified(email)
          return
        }
        this.otpError = res.data?.message || 'Invalid verification code.'
      } catch (e) {
        this.otpError = e.response?.data?.message || 'Verification failed.'
      } finally {
        this.otpVerifying = false
      }
    },
    register() {
      this.error = ''
      if (this.password !== this.confirmPassword) {
        this.error = 'Password and confirm password must match.'
        return
      }
      const email = String(this.userEmail || '').trim().toLowerCase()
      if (!this.emailVerified || this.verifiedEmailKey !== email) {
        this.error = 'Please verify your email before registering.'
        return
      }
      this.$refs.registerForm.validate().then(async success => {
        if (!success || this.isRegistering) return
        this.isRegistering = true
        try {
          const res = await axios.post(jwtDefaultConfig.registerEndpoint, {
            email: this.userEmail,
            password: this.password,
            compny_name: this.companyName,
            contact_person_name: this.contactPersonName,
            adress: this.address,
            pincode: this.pinCode,
            country: this.country,
            state_of_incorporation: String(this.stateOfIncorporation || '').trim(),
            terms_acceptance: true,
            age_confirmation: true,
          })
          if (res.data?.success) {
            this.showRegisteredModal = true
          } else {
            this.error = res.data?.message || 'Registration failed.'
          }
        } catch (error) {
          this.error = error.response?.data?.message || 'Registration failed.'
        } finally {
          this.isRegistering = false
        }
      })
    },
    onRegisteredPrimary() {
      this.$router.push({ name: 'auth-login' })
    },
  },
}
</script>

<style scoped lang="scss">
@import '@core/scss/vue/pages/page-auth.scss';

.register-page {
  .register-left {
    background: #fcfcfd;
  }

  .register-left-art {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1rem 1.5rem;
  }

  .register-illustration {
    width: 100%;
    max-width: 520px;
  }

  .register-form-wrap {
    max-width: 490px;
  }

  .register-title {
    color: #4b465c;
    font-weight: 700;
    font-size: 2.05rem;
    line-height: 1.1;
  }

  .register-subtitle {
    color: #6e6b7b;
    font-size: 1.02rem;
  }

  .register-form {
    .form-group label {
      color: #3f3b57;
      font-weight: 500;
      font-size: 0.9rem;
      margin-bottom: 0.32rem;
    }

    .form-control {
      min-height: 38px;
      border-color: #ececf3;
      font-size: 0.93rem;
    }
  }

  .compact-row {
    margin-left: -0.35rem;
    margin-right: -0.35rem;

    > [class*='col-'] {
      padding-left: 0.35rem;
      padding-right: 0.35rem;
    }
  }

  .verify-btn {
    background: #000 !important;
    border-color: #000 !important;
    color: #fff !important;
    font-weight: 600;
    min-height: 38px;
    font-size: 0.88rem;
    margin-bottom: 0.5rem;
  }

  .register-submit-btn {
    background: #000 !important;
    border-color: #000 !important;
    color: #fff !important;
    min-height: 42px;
    font-size: 1.06rem;
    font-weight: 700;
  }

  .register-login-text {
    margin-top: 1.25rem;
  }

  @media (max-width: 991.98px) {
    .register-title {
      font-size: 1.65rem;
    }
  }
}
</style>

<style lang="scss">
.register-otp-modal {
  z-index: 1060 !important;
}
</style>
